/**
 * Access helpers for the internal Photo Checks admin surfaces.
 * These gate platform AI tooling (evaluation, prompt versioning, format recognition).
 * Maps to existing AI_TRIAGE_* permission strings on the user roles array.
 */

export type PhotoChecksUser = {
  role?: string | null;
  roles?: string[] | null;
} | null | undefined;

function roleList(user: PhotoChecksUser): string[] {
  const roles = Array.isArray(user?.roles) ? user!.roles!.map(String) : [];
  const legacy = typeof user?.role === 'string' ? user.role : '';
  return legacy ? [...roles, legacy] : roles;
}

export function isPhotoChecksSuperAdmin(user: PhotoChecksUser): boolean {
  const roles = roleList(user);
  return roles.includes('superadmin') || roles.includes('super_admin');
}

export function hasPhotoChecksPermission(user: PhotoChecksUser, permission: string): boolean {
  if (isPhotoChecksSuperAdmin(user) && permission.startsWith('AI_TRIAGE_')) {
    return true;
  }
  return roleList(user).includes(permission);
}

export function canViewPhotoChecks(user: PhotoChecksUser): boolean {
  // Align with server: admin role alone is not enough; AI_TRIAGE_VIEW (or superadmin) required.
  return isPhotoChecksSuperAdmin(user)
    || hasPhotoChecksPermission(user, 'AI_TRIAGE_VIEW');
}

export function canEditPhotoChecks(user: PhotoChecksUser): boolean {
  return hasPhotoChecksPermission(user, 'AI_TRIAGE_PROMPT_EDIT');
}

export function canPublishPhotoChecks(user: PhotoChecksUser): boolean {
  return hasPhotoChecksPermission(user, 'AI_TRIAGE_PROMPT_PUBLISH');
}

/** Specialist: Superadmin or AI_TRIAGE_RUN (verification/config write). */
export function canAccessPhotoChecksAdvanced(user: PhotoChecksUser): boolean {
  return isPhotoChecksSuperAdmin(user)
    || hasPhotoChecksPermission(user, 'AI_TRIAGE_RUN');
}

export const PHOTO_RULE_LABELS: Record<string, string> = {
  POSTING: 'Posting photo standards',
  REMOVAL: 'Removal photo standards',
  REPOST: 'Repost photo standards',
};

export function photoRuleLabel(submissionType: string): string {
  return PHOTO_RULE_LABELS[submissionType] ?? `${submissionType} photo standards`;
}

/** Server meta: live publish is off until PHOTO_CHECKS_LIVE_PUBLISH_ENABLED. */
export type PhotoChecksMeta = {
  ok: true;
  livePublishEnabled: boolean;
  sandboxMode: boolean;
};

export function canMakePhotoChecksLive(
  user: PhotoChecksUser,
  livePublishEnabled: boolean | undefined,
): boolean {
  return canPublishPhotoChecks(user) && livePublishEnabled === true;
}
